import { BattingStats, PitchData, PitchingStats, PitchReprocessed, ResultData, ScoringData }  from '../enums/index.js';
import {
    classifiesAsStrikeout as defaultStrikeOutClassifier,
    classifiesAsHit as defaultHitClassifier,
} from '../classificators/plateAppearanceResult/index.js';
import {
    classifiesAsPitch,
    classifiesAsSwing as defaultSwingClassifier,
    classifiesAsWhiff as defaultWhiffClassifier
} from '../classificators/pitchCall/index.js';
import PlateDisciplineStatsCalculator from './PlateDisciplineStatsCalculator.js';

/**
 * Calculates stats often named as Plate Discipline
 * @see https://www.fantraxhq.com/sabermetric-series-part-3-plate-discipline/
 * @see https://library.fangraphs.com/offense/plate-discipline/
 */
class UniversalPlateDisciplineStatsCalculator extends PlateDisciplineStatsCalculator
{
    constructor(
        hitClassifier, strikeOutClassifier, swingClassifier, whiffClassifier, kZoneClassifier,
        pitchesClassifier, oldResultFallbackCalculator
    ) {
        super(
            hitClassifier || defaultHitClassifier,
            strikeOutClassifier || defaultStrikeOutClassifier,
            swingClassifier || defaultSwingClassifier,
            whiffClassifier || defaultWhiffClassifier,
            kZoneClassifier // this is actually the same
        );

        if (typeof(oldResultFallbackCalculator) !== 'undefined' && !(oldResultFallbackCalculator instanceof PlateDisciplineStatsCalculator)) {
            throw new TypeError(`oldResultFallbackCalculator must be an instance of PlateDisciplineStatsCalculator class or undefined to use default implementation`);
        }
        this.oldResultFallbackCalculator = oldResultFallbackCalculator || new PlateDisciplineStatsCalculator();

        if (typeof(pitchesClassifier) !== 'undefined' && typeof(pitchesClassifier) !== 'function') {
            throw new TypeError(`pitchesClassifier must be an predicate taking single argument - pitch call. Pass undefined to use default implementation`);
        }

        this.pitchesClassifier = pitchesClassifier || classifiesAsPitch;
    }

    calculatePlateDiscipline(pitchingResults, includePercentages, pitchingContext) {
        if (!(pitchingResults instanceof Array)) {
            throw new TypeError('pitchingResults must be an instance of array');
        }

        if (typeof(includePercentages) === 'undefined') {
            includePercentages = true;
        }
        else if(typeof(includePercentages) !== 'boolean') {
            throw new TypeError('includePercentages must be an boolean type');
        }

        if (typeof(pitchingContext) === 'undefined') {
            pitchingContext = true;
        }
        else if(typeof(pitchingContext) !== 'boolean') {
            throw new TypeError('pitchingContext must be an boolean type');
        }

        const StatsEnum = pitchingContext ? PitchingStats: BattingStats;

        const plateDiscipline = {
            [StatsEnum.NumberOfPitches.key]: 0,
            PitchesWithStrikeZonePosition: 0,
            KZone: 0,
            OZone: 0,
            [StatsEnum.Strikeouts.key]: 0,
            [StatsEnum.Swings.key]: 0,
            ZSwings: 0,
            ZContacts: 0,
            ZWhiffs: 0,
            OSwings: 0,
            OContacts: 0,
            OWhiffs: 0,
            [StatsEnum.Whiffs.key]: 0,
            [StatsEnum.Contacts.key]: 0,
        };

        if (pitchingContext) {
            plateDiscipline[PitchingStats.BattersFaced.key] = 0;
            plateDiscipline[PitchingStats.HitsAllowed.key] = 0;
        }
        else {
            plateDiscipline[BattingStats.PlateAppearances.key] = 0;
            plateDiscipline[BattingStats.Hits.key] = 0;
        }

        const battersFacedOrPa = [];

        //let's go through pitchingResults
        for (let i = 0; i < pitchingResults.length; i++) {
            const result = pitchingResults[i];
            if (typeof(result) !== 'object' || result === null) {
                throw new TypeError(`Result at index ${i} is not an object or null`);
            }

            const resultData = result[ResultData.Data.key];
            if (typeof(resultData) !== 'object' || resultData === null) {
                throw new TypeError(`Result.Data at index ${i} is not an object or null`);
            }

            const batterSessionPaId = this.getBatterId(result);

            if (!batterSessionPaId) {
                throw new TypeError(`Unable to determine batter/session/PA id at index ${i}`);
            }

            // TODO - better plate appearance check
            if (battersFacedOrPa.indexOf(batterSessionPaId) < 0){
                battersFacedOrPa.push(batterSessionPaId);
                if (pitchingContext) {
                    plateDiscipline[PitchingStats.BattersFaced.key]++;
                }
                else {
                    plateDiscipline[BattingStats.PlateAppearances.key]++;
                }
            }

            let swing = false, whiff = false;
            const paResult = resultData[ScoringData.PlateAppearanceResult.key];
            const pitchCall = resultData[ScoringData.PitchCall.key];
            const pitchResult = resultData[PitchData.Result.key];

            if (pitchCall && paResult) {
                if (!this.pitchesClassifier(pitchCall)) {
                    // not a pitch: balk or throw to the baseman - "In baseball, the pitch is the act of throwing the baseball toward home plate to start a play."
                    // see https://en.wikipedia.org/wiki/Pitch_(baseball)
                    // NOTE - unidentified pitch is assumed to be regular pitch, as these are quite rare
                    continue;
                }

                if (this.hitClassifier(paResult)) {
                    if (pitchingContext) {
                        plateDiscipline[PitchingStats.HitsAllowed.key]++;
                    }
                    else {
                        plateDiscipline[BattingStats.Hits.key]++;
                    }
                }

                if (this.strikeOutClassifier(paResult)) {
                    plateDiscipline[StatsEnum.Strikeouts.key]++;
                }

                if (this.swingClassifier(pitchCall)) {
                    swing = true;
                    plateDiscipline[StatsEnum.Swings.key]++;
                }

                if (this.whiffClassifier(pitchCall)) {
                    whiff = true;
                    plateDiscipline[StatsEnum.Whiffs.key]++;
                }
                else if (swing) {
                    plateDiscipline[StatsEnum.Contacts.key]++;
                }
            }
            else {
                if (this.oldResultFallbackCalculator.hitClassifier(pitchResult)) {
                    if (pitchingContext) {
                        plateDiscipline[PitchingStats.HitsAllowed.key]++;
                    }
                    else {
                        plateDiscipline[BattingStats.Hits.key]++;
                    }
                }

                if (this.oldResultFallbackCalculator.strikeOutClassifier(pitchResult)) {
                    plateDiscipline[StatsEnum.Strikeouts.key]++;
                }

                if (this.oldResultFallbackCalculator.swingClassifier(pitchResult)) {
                    swing = true;
                    plateDiscipline[StatsEnum.Swings.key]++;
                }

                if (this.oldResultFallbackCalculator.whiffClassifier(pitchResult)) {
                    whiff = true;
                    plateDiscipline[StatsEnum.Whiffs.key]++;
                }
                else if (swing) {
                    plateDiscipline[StatsEnum.Contacts.key]++;
                }
            }

            plateDiscipline[StatsEnum.NumberOfPitches.key]++;

            if (this.kZoneClassifier(resultData[PitchReprocessed.StrikeZonePositionR.key])) {
                plateDiscipline.KZone++;
                plateDiscipline.PitchesWithStrikeZonePosition++;

                if (swing) {
                    plateDiscipline.ZSwings++;

                    if (!whiff) {
                        plateDiscipline.ZContacts++;
                    }
                    else {
                        plateDiscipline.ZWhiffs++;
                    }
                }
            }
            else if (resultData[PitchReprocessed.StrikeZonePositionR.key] !== null) {
                plateDiscipline.OZone++;
                plateDiscipline.PitchesWithStrikeZonePosition++;

                if (swing) {
                    plateDiscipline.OSwings++;

                    if (!whiff) {
                        plateDiscipline.OContacts++;
                    }
                    else {
                        plateDiscipline.OWhiffs++;
                    }
                }
            }
        }

        if (includePercentages) {
           return Object.assign(plateDiscipline, this.calculatePlateDisciplinePercentages(plateDiscipline, pitchingContext));
        }

        return plateDiscipline;
    }
}

export default UniversalPlateDisciplineStatsCalculator;